import { generatePath } from 'react-router-dom'
import { GridCellProps, GridColumnProps } from '@progress/kendo-react-grid'
import { TradingAccount, WalletTransaction } from 'api/Transaction/Types/Transaction'
import { InputFilterCell } from 'components/Filter'
import { Link } from 'components/Link'
import { generateClassNameForDebitOrCreditTransactionType } from 'helpers/transactions'
import { links } from 'utils/featureToggles'

export interface GridColumn extends GridColumnProps {
  show: boolean
  width?: number
}

export const transactionDetailsAdjustmentTradingAccountColumns: GridColumn[] = [
  {
    title: 'Type',
    field: 'Type',
    cell: (props: GridCellProps): JSX.Element => {
      if (!props.dataItem?.directionType?.name) {
        return <td />
      }
      return (
        <td
          className={generateClassNameForDebitOrCreditTransactionType(props.dataItem.directionType)}
        >
          {props.dataItem.directionType.name}
        </td>
      )
    },
    show: true,
    filterable: false,
    filter: 'text',
    width: 100,
  },
  {
    title: 'Client',
    field: 'wallet.client.name',
    filterCell: InputFilterCell,
    cell: (props: GridCellProps): JSX.Element => {
      const walletOrTradingAccountTransaction = props.dataItem as WalletTransaction | TradingAccount
      if ('paymentAgentTransaction' in walletOrTradingAccountTransaction) {
        const paymentAgentTransaction = props.dataItem.paymentAgentTransaction
        if (!paymentAgentTransaction) {
          return (
            <td style={{ color: 'var(--link)', textDecoration: 'underline' }}>
              <Link
                to={{
                  pathname: generatePath(links.recordsClients.detailsUrl as string, {
                    id: walletOrTradingAccountTransaction.wallet.client.id,
                    slug: 'details',
                  }),
                }}
              >
                {walletOrTradingAccountTransaction.wallet.client.name}
              </Link>
            </td>
          )
        } else {
          return (
            <td style={{ color: 'var(--link)', textDecoration: 'underline' }}>
              <Link
                to={{
                  pathname: generatePath(links.recordsPaymentAgents.detailsUrl as string, {
                    id: paymentAgentTransaction.paymentAgent.id,
                    slug: 'details',
                  }),
                }}
              >
                {paymentAgentTransaction.paymentAgent.name}
              </Link>
            </td>
          )
        }
      }
      if ('client' in walletOrTradingAccountTransaction) {
        return (
          <td style={{ color: 'var(--link)', textDecoration: 'underline' }}>
            <Link
              to={{
                pathname: generatePath(links.recordsClients.detailsUrl as string, {
                  id: walletOrTradingAccountTransaction.client.id,
                  slug: 'details',
                }),
              }}
            >
              {walletOrTradingAccountTransaction.client.name}
            </Link>
          </td>
        )
      }
      if ('wallet' in walletOrTradingAccountTransaction) {
        return (
          <td style={{ color: 'var(--link)', textDecoration: 'underline' }}>
            <Link
              to={{
                pathname: generatePath(links.recordsClients.detailsUrl as string, {
                  id: walletOrTradingAccountTransaction.wallet.client.id,
                  slug: 'details',
                }),
              }}
            >
              {walletOrTradingAccountTransaction.wallet.client.name}
            </Link>
          </td>
        )
      }
      return <td />
    },
    show: true,
    filterable: true,
    width: 220,
  },
  {
    title: 'Trading Account',
    field: 'tradingAccount.name',
    filterCell: InputFilterCell,
    cell: (props: GridCellProps): JSX.Element => {
      const tradingAccountTransaction = props.dataItem as TradingAccount

      if (!tradingAccountTransaction?.tradingAccount?.name) {
        return <td />
      }

      return (
        <td style={{ color: 'var(--link)', textDecoration: 'underline' }}>
          <Link
            to={{
              pathname: generatePath(links.recordsTradingAccounts.detailsUrl as string, {
                id: tradingAccountTransaction.tradingAccount.id,
                slug: 'details',
              }),
            }}
          >
            {tradingAccountTransaction.tradingAccount.name}
          </Link>
        </td>
      )
    },
    show: true,
    filterable: true,
    filter: 'text',
  },
  {
    title: 'Amount',
    field: 'amount',
    filterCell: InputFilterCell,
    cell: (props: GridCellProps): JSX.Element => {
      if (props.dataItem?.amount >= 0) {
        return <td>{props.dataItem.amount.toFixed(2)}</td>
      }
      return <td />
    },
    show: true,
    filterable: true,
    filter: 'text',
  },
  {
    title: 'Gross amount',
    field: 'grossAmount',
    filterCell: InputFilterCell,
    cell: (props: GridCellProps): JSX.Element => {
      if (props.dataItem?.amount >= 0) {
        return <td>{props.dataItem.grossAmount.toFixed(2)}</td>
      }
      return <td />
    },
    show: true,
    filterable: true,
    filter: 'text',
  },
  {
    title: 'Currency',
    field: 'currency.id',
    cell: (props: GridCellProps): JSX.Element => {
      if (!props.dataItem?.currency?.id) {
        return <td />
      }
      return <td>{props.dataItem.currency.id}</td>
    },
    show: true,
    filterable: false,
    filter: 'text',
  },
]
